import React from 'react';
import Box from '@mui/material/Box';
import AuthContainer from './AuthContainer';
import LogoContainer from './LogoContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { getAppDisplayName, getAppName } from 'src/utils/app';
import colors from 'src/utils/colors';

const SUPPORT_EMAIL = 'support@stackline.com';

/** The `error` the API tags a no-workspace refusal with (NoWorkspaceAccessException.Code). */
export const NO_WORKSPACE_ERROR = 'no_workspace_access';

/** True when this failure is "credential fine, nothing to open" rather than a bad credential. */
export const isNoWorkspaceError = (err: any) => err?.response?.data?.error === NO_WORKSPACE_ERROR;

interface NoWorkspaceProps {
  /** The address that just authenticated, so the user can see which account this is about. */
  email?: string;
  /** Back to the email step. */
  onStartOver: () => void;
}

/**
 * Authenticated, but there is nothing to open.
 *
 * Reached when the credential checks out and the account has no usable division
 * membership — never provisioned, or every membership deactivated. Both look the
 * same from here and get the same screen.
 *
 * This is NOT a signed-in state. With no division the API cannot mint a JWT, so
 * no token or cookie exists at this point; it is a terminal screen with a way
 * out. It replaces a 401 that told the user their verification code was invalid
 * when the code had in fact been accepted, which sent people back to retry a
 * code that was never the problem.
 */
const NoWorkspace: React.FC<NoWorkspaceProps> = ({ email, onStartOver }) => {
  const title = getAppDisplayName(getAppName());
  const subject = encodeURIComponent('No workspace access');
  const body = encodeURIComponent(
    `I signed in${email ? ` as ${email}` : ''} and was told my account isn't part of a workspace.`
  );

  return (
    <AuthContainer>
      <Flex flexDirection="column" alignItems="center" gap={32}>
        <LogoContainer title={title} />

        <Flex flexDirection="column" alignItems="center" gap={16}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '999px',
              background: '#ffffff',
              border: `1px solid ${colors.graysSecondary}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke={colors.graysSecondaryText}
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 21h18" />
              <path d="M5 21V6l7-3 7 3v15" />
              <path d="M10 21v-5h4v5" />
            </svg>
          </Box>

          <Text
            variant="h4"
            textAlign="center"
            sx={{ fontFamily: "'Geist'", fontSize: '26px', fontWeight: 600, color: colors.darkBlue }}
          >
            No workspace access
          </Text>

          <Text
            variant="body2"
            textAlign="center"
            sx={{ fontFamily: "'Geist'", fontSize: '15px', lineHeight: 1.6, color: colors.graysSecondaryText }}
          >
            We verified{' '}
            <span style={{ color: colors.darkBlue, fontWeight: 500 }}>{email || 'your email'}</span>, but this
            account isn&apos;t part of a workspace yet — so there&apos;s nothing for us to open.
          </Text>

          <Text
            variant="body2"
            textAlign="center"
            sx={{ fontFamily: "'Geist'", fontSize: '15px', lineHeight: 1.6, color: colors.graysSecondaryText }}
          >
            An admin on your company&apos;s account can add you. If you think this is wrong, we can look into it.
          </Text>
        </Flex>

        <Flex flexDirection="column" gap={12} style={{ width: '100%' }}>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`}
            style={{
              width: '100%',
              height: '47px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: colors.darkBlue,
              color: '#ffffff',
              borderRadius: '999px',
              fontFamily: "'Geist'",
              fontSize: '18px',
              fontWeight: 600,
              textDecoration: 'none'
            }}
          >
            Contact support
          </a>

          <button
            type="button"
            onClick={onStartOver}
            style={{
              width: '100%',
              height: '47px',
              background: 'transparent',
              color: colors.darkBlue,
              border: `1px solid ${colors.graysSecondary}`,
              borderRadius: '999px',
              fontFamily: "'Geist'",
              fontSize: '15px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Try a different email
          </button>
        </Flex>
      </Flex>
    </AuthContainer>
  );
};

export default NoWorkspace;
